import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useField } from 'formik';
import { formatDate } from '@koob/dates';
import { KoobButton, KoobCard, KoobEmptyState, KoobAlert } from '@koob/ui';
import { EXPERIENCE_ACCOMMODATION_SCHEMA } from '../../Utilities/ExperienceValidationSchemas';
import SectionHeader from './SectionHeader';
import CategoryRow from './CategoryRow';
import RoomRow from './RoomRow';
import SupplementsGrid from './SupplementsGrid';

export function ExperienceHotelRoomConfig({ isReadOnly = false }) {
  const { t } = useTranslation('experiences');
  const [categoriesField, , categoriesHelpers] = useField(
    'accommodationCategories',
  );
  const [roomsField, , roomsHelpers] = useField('accommodationRooms');
  const [periodsField] = useField('periods');
  const [{ value: currency }] = useField('currency');

  const categories = categoriesField.value || [];
  const rooms = roomsField.value || [];
  const periods = periodsField.value || [];

  // Ensure every category has price entries for every period × room combination
  useEffect(() => {
    if (categories.length === 0 || rooms.length === 0) return;
    const activePeriods = periods.filter((p) => p.id);
    if (activePeriods.length === 0) return;

    let needsUpdate = false;
    const updatedCategories = categories.map((cat) => {
      const existingPrices = cat.prices || [];
      const newPrices = [...existingPrices];

      activePeriods.forEach((period) => {
        rooms.forEach((_, roomIdx) => {
          const exists = newPrices.some(
            (p) => p.roomIndex === roomIdx && p.periodId === period.id,
          );
          if (!exists) {
            // Check if there's a legacy price (no periodId) for this room
            const legacyIdx = newPrices.findIndex(
              (p) => p.roomIndex === roomIdx && !p.periodId,
            );
            if (legacyIdx !== -1) {
              // Upgrade legacy price by adding periodId
              newPrices[legacyIdx] = {
                ...newPrices[legacyIdx],
                periodId: period.id,
              };
            } else {
              newPrices.push({
                roomIndex: roomIdx,
                supplement: 0,
                supplementType: 'value',
                periodId: period.id,
              });
            }
            needsUpdate = true;
          }
        });
      });

      return { ...cat, prices: newPrices };
    });

    if (needsUpdate) {
      categoriesHelpers.setValue(updatedCategories);
    }
  }, [periods.length, rooms.length, categories.length]);

  useEffect(() => {
    if (!rooms || rooms.length === 0) return;

    const hasUninitializedRooms = rooms.some(
      (room) =>
        room.adultsCount === undefined || room.childrenCount === undefined,
    );

    if (hasUninitializedRooms) {
      const sanitizedRooms = rooms.map((room) => {
        const pax = room.paxCount ?? 1;
        return {
          ...room,
          paxCount: pax,
          adultsCount: room.adultsCount ?? pax,
          childrenCount: room.childrenCount ?? 0,
        };
      });
      roomsHelpers.setValue(sanitizedRooms);
    }
  }, [rooms, roomsHelpers]);

  const addCategory = () => {
    const prices = [];
    const activePeriods = periods.filter((p) => p.id);
    if (activePeriods.length > 0) {
      activePeriods.forEach((period) => {
        rooms.forEach((_, idx) => {
          prices.push({
            roomIndex: idx,
            supplement: 0,
            supplementType: 'value',
            periodId: period.id,
          });
        });
      });
    } else {
      rooms.forEach((_, idx) => {
        prices.push({
          roomIndex: idx,
          supplement: 0,
          supplementType: 'value',
        });
      });
    }
    const newCategory = {
      name: '',
      sortOrder: categories.length,
      prices,
    };
    categoriesHelpers.setValue([...categories, newCategory]);
  };

  const removeCategory = (index) => {
    const updated = categories.filter((_, i) => i !== index);
    categoriesHelpers.setValue(updated);
  };

  const updateCategoryName = (index, name) => {
    const updated = [...categories];
    updated[index] = { ...updated[index], name };
    categoriesHelpers.setValue(updated);
  };

  const addRoom = () => {
    const nextPaxCount =
      rooms.length > 0 ? Math.max(...rooms.map((r) => r.paxCount || 0)) + 1 : 1;
    const defaultNames = {
      1: 'Single',
      2: 'Double',
      3: 'Triple',
      4: 'Quadruple',
    };
    const newRoom = {
      name: defaultNames[nextPaxCount] || `${nextPaxCount} pax`,
      paxCount: nextPaxCount,
      adultsCount: nextPaxCount,
      childrenCount: 0,
      sortOrder: rooms.length,
    };
    roomsHelpers.setValue([...rooms, newRoom]);

    const activePeriods = periods.filter((p) => p.id);
    const newPrices =
      activePeriods.length > 0
        ? activePeriods.map((period) => ({
            roomIndex: rooms.length,
            supplement: 0,
            supplementType: 'value',
            periodId: period.id,
          }))
        : [{ roomIndex: rooms.length, supplement: 0, supplementType: 'value' }];

    const updatedCategories = categories.map((cat) => ({
      ...cat,
      prices: [...(cat.prices || []), ...newPrices],
    }));
    categoriesHelpers.setValue(updatedCategories);
  };

  const removeRoom = (roomIndex) => {
    const updatedRooms = rooms.filter((_, i) => i !== roomIndex);
    roomsHelpers.setValue(updatedRooms);

    const updatedCategories = categories.map((cat) => ({
      ...cat,
      prices: (cat.prices || [])
        .filter((p) => p.roomIndex !== roomIndex)
        .map((p) => ({
          ...p,
          roomIndex: p.roomIndex > roomIndex ? p.roomIndex - 1 : p.roomIndex,
        })),
    }));
    categoriesHelpers.setValue(updatedCategories);
  };

  const updateRoomName = (index, name) => {
    const updated = [...rooms];
    updated[index] = { ...updated[index], name };
    roomsHelpers.setValue(updated);
  };

  const updateRoomPaxCount = (index, value) => {
    const updated = [...rooms];
    const parsed = parseInt(value, 10);
    const paxCount = isNaN(parsed) ? '' : parsed;
    const room = updated[index];

    let adultsCount = room.adultsCount ?? paxCount;
    let childrenCount = room.childrenCount ?? 0;

    if (paxCount !== '' && paxCount > 0) {
      if (adultsCount > paxCount) adultsCount = paxCount;
      if ((adultsCount || 0) + (childrenCount || 0) > paxCount) {
        childrenCount = Math.max(0, paxCount - (adultsCount || 0));
      }
    } else {
      adultsCount = 0;
      childrenCount = 0;
    }

    updated[index] = { ...room, paxCount, adultsCount, childrenCount };
    roomsHelpers.setValue(updated);
  };

  const updateRoomAdultsCount = (index, value) => {
    const updated = [...rooms];
    const parsed = parseInt(value, 10);
    let adultsCount = isNaN(parsed) ? '' : parsed;
    const room = updated[index];
    const pax = Number(room.paxCount) || 0;

    let childrenCount = room.childrenCount ?? 0;

    if (adultsCount !== '' && adultsCount >= 0) {
      if (adultsCount > pax) adultsCount = pax;
      if (adultsCount + childrenCount > pax) {
        childrenCount = Math.max(0, pax - adultsCount);
      }
      if (adultsCount === 0) childrenCount = 0;
    } else {
      childrenCount = 0;
    }

    updated[index] = { ...room, adultsCount, childrenCount };
    roomsHelpers.setValue(updated);
  };

  const updateRoomChildrenCount = (index, value) => {
    const updated = [...rooms];
    const parsed = parseInt(value, 10);
    let childrenCount = isNaN(parsed) ? '' : parsed;
    const room = updated[index];
    const pax = Number(room.paxCount) || 0;
    const adults = Number(room.adultsCount) || 0;

    if (childrenCount !== '' && childrenCount >= 0) {
      const maxAllowedChildren = Math.max(0, pax - adults);
      if (childrenCount > maxAllowedChildren) {
        childrenCount = maxAllowedChildren;
      }
    }

    updated[index] = { ...room, childrenCount };
    roomsHelpers.setValue(updated);
  };

  return (
    <div className="w-full mt-4 space-y-5">
      <KoobAlert
        icon="fa-info-circle"
        variant="info"
        title={t('accommodation.infoTitle', 'Partner integration')}
        description={t(
          'accommodation.info',
          'This tab is reserved for the integration with certain partners.',
        )}
      />

      <KoobCard>
        <SectionHeader
          title={t('accommodation.hotelCategories', 'Hotel categories')}
          subtitle={t(
            'accommodation.hotelCategoriesSubtitle',
            'Define hotel star ratings or quality tiers.',
          )}
          action={
            !isReadOnly && (
              <KoobButton variant="primary" size="sm" onClick={addCategory}>
                <div className="flex items-center gap-1.5">
                  <i
                    className="fa-light fa-circle-plus"
                    style={{ fontSize: 16 }}
                  />
                  {t('accommodation.addCategory', 'Add category')}
                </div>
              </KoobButton>
            )
          }
        />

        {categories.length === 0 ? (
          <KoobEmptyState
            icon={<i className="fal fa-hotel text-2xl" />}
            description={t(
              'accommodation.noCategoriesYet',
              'No hotel categories defined yet. Add a category to get started.',
            )}
          />
        ) : (
          <div className="space-y-2">
            {categories.map((category, catIndex) => (
              <CategoryRow
                key={catIndex}
                category={category}
                catIndex={catIndex}
                updateCategoryName={updateCategoryName}
                removeCategory={removeCategory}
                t={t}
                isReadOnly={isReadOnly}
              />
            ))}
          </div>
        )}
      </KoobCard>

      <KoobCard>
        <SectionHeader
          title={t('accommodation.roomCategories', 'Room categories')}
          subtitle={t(
            'accommodation.roomCategoriesSubtitle',
            'Configure room types and their occupancy (number of guests per room).',
          )}
          action={
            !isReadOnly && (
              <KoobButton variant="primary" size="sm" onClick={addRoom}>
                <div className="flex items-center gap-1.5">
                  <i
                    className="fa-light fa-circle-plus"
                    style={{ fontSize: 16 }}
                  />
                  {t('accommodation.addRoom', 'Add room category')}
                </div>
              </KoobButton>
            )
          }
        />

        {rooms.length === 0 ? (
          <KoobEmptyState
            icon={<i className="fal fa-bed text-2xl" />}
            description={t(
              'accommodation.noRoomsYet',
              'No room categories defined yet. Add a room type to get started.',
            )}
          />
        ) : (
          <div className="space-y-2">
            {rooms.map((room, roomIndex) => (
              <RoomRow
                key={roomIndex}
                room={room}
                roomIndex={roomIndex}
                updateRoomName={updateRoomName}
                updateRoomPaxCount={updateRoomPaxCount}
                updateRoomAdultsCount={updateRoomAdultsCount}
                updateRoomChildrenCount={updateRoomChildrenCount}
                removeRoom={removeRoom}
                t={t}
                isReadOnly={isReadOnly}
              />
            ))}
          </div>
        )}
      </KoobCard>

      {categories.length > 0 && rooms.length > 0 && (
        <>
          {periods.filter((p) => p.id).length > 0 ? (
            <div className="space-y-6">
              <SectionHeader
                title={`${t('accommodation.pricing', 'Pricing')}`}
                subtitle={t('accommodation.pricingInstructions')}
              />
              <div className="space-y-4">
                {periods
                  .filter((p) => p.id)
                  .map((period, periodIndex) => (
                    <KoobCard key={period.id || periodIndex}>
                      <SectionHeader
                        title={`${t(
                          'accommodation.period',
                          'Period',
                        )} — ${formatDate(
                          period.startAt,
                          'DD/MM/YYYY',
                        )} → ${formatDate(period.endAt, 'DD/MM/YYYY')}`}
                      />

                      <SupplementsGrid
                        categories={categories}
                        rooms={rooms}
                        period={period}
                        currency={currency}
                        t={t}
                        isReadOnly={isReadOnly}
                      />
                    </KoobCard>
                  ))}
              </div>
            </div>
          ) : (
            <KoobCard>
              <SectionHeader
                title={t('accommodation.pricing', 'Pricing')}
                subtitle={t(
                  'accommodation.pricingSubtitle',
                  'The price for a 3-star double room is the standard rate. Add a supplement for any other available room category / occupancy. Uncheck to disable a combination.',
                )}
              />

              <SupplementsGrid
                categories={categories}
                rooms={rooms}
                t={t}
                isReadOnly={isReadOnly}
              />
            </KoobCard>
          )}
        </>
      )}
    </div>
  );
}

export const getSchema = () => EXPERIENCE_ACCOMMODATION_SCHEMA;
